import { Input } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { EmployeeDisplayTinyInfo, MeetingRequestDto, MeetingStepViewId, MeetingType } from '../../../types'
import { TimeZoneAndLocale } from '../../../types.custom'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useAuth } from '../../../context/auth-context'
import { InviteModalMeetingOverlap } from '../../../components/custom_components/InviteModalContent/InviteModalMeetingOverlap'
import { InviteModalRow } from '../../../components/custom_components/InviteModalContent/InviteModalRow'
import PerText from '../../../components/custom_components/Text'
import PerDatePicker from '../../../components/custom_components/DatePicker'
import PerTimePicker from '../../../components/custom_components/TimePicker'
import { MeetingInviteDurationSelect } from '../../../components/custom_components/InviteModalContent/MeetingInviteDurationSelect'
import { MeetingInviteOptionsSelect } from '../../../components/custom_components/InviteModalContent/MeetingInviteOptionsSelect'
import { AppraisalSeasonRow } from '../../../components/custom_components/InviteModalContent/AppraisalSeasonRow'
import { NonParticipantsSelect } from '../../../components/custom_components/InviteModalContent/NonParticipantsSelect'
import { AppraisalInviteSteps } from '../../../components/custom_components/InviteModalContent/AppraisalInviteSteps'
import { AppraisalUpdateInviteSteps } from '../../../components/custom_components/InviteModalContent/AppraisalUpdateInviteSteps'
import { ReducedStepsReasonSelect } from '../../../components/custom_components/InviteModalContent/ReducedStepsReasonSelect'
import RichTextBox from '../../../components/custom_components/RichTextBox'
import { InviteModalContentUtils } from '../../../components/custom_components/InviteModalContent/inviteModalContentUtils'
import { StatusMessageBox } from '../../../components/custom_components/StatusMessageBox'
import { ValidationErrorsData } from '../../../shared/graphqlErrorHelper'
import { DashboardAppraisalInviteModalState } from './DasboardAppraisalInviteModal'

export type EmployeeShort = {
  id: number
  legalFirstName: string
  legalLastName: string
}

export type AppraisalInviteModalContentProps = {
  employee: EmployeeDisplayTinyInfo
  state: DashboardAppraisalInviteModalState
  timelocal: TimeZoneAndLocale
  inviteModalContentUtils: InviteModalContentUtils
  onUpdate: (fieldname: string, value: unknown) => void
  onUpdateSteps: (stepId: MeetingStepViewId, value: boolean) => void
  requestedMeeting?: MeetingRequestDto
  serverError?: ValidationErrorsData[]
}

const DashboardAppraisalInviteModalContent: FC<AppraisalInviteModalContentProps> = ({
  employee,
  state,
  timelocal,
  onUpdate,
  onUpdateSteps,
  inviteModalContentUtils,
  serverError,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()

  const {
    meetingType,
    meetingDate,
    location,
    emailText,
    viewIds,
    reasonForReducedSteps,
    duration,
    additionalParticipants,
    additionalParticipantsOptions,
    appraisalSeason,
  } = state

  const containsStepViewId = (stepId: MeetingStepViewId): boolean => {
    if (viewIds) {
      return !!viewIds.find((x) => x === stepId)
    }
    return false
  }

  const appraisalWithReducedSteps =
    meetingType === MeetingType.Appraisal &&
    !(containsStepViewId(MeetingStepViewId.SelfReflection) && containsStepViewId(MeetingStepViewId.Evaluation))

  const isAppraisalMeeting = meetingType === MeetingType.Appraisal
  const isAppraisalUpdateMeeting = meetingType === MeetingType.AppraisalUpdate

  const agendaMargin = () => {
    if (appraisalWithReducedSteps) {
      return undefined
    } else {
      return 'bigger'
    }
  }

  return (
    <>
      <InviteModalRow label={t('meetingInvite.meetingInviteEmployee')}>
        <PerText>{computeFullname(user.nameDisplayPreference, employee.firstName, employee.lastName)}</PerText>
      </InviteModalRow>

      <div data-testid="invite-options-wrapper">
        <>
          <InviteModalRow label={t('meetingInvite.meetingDate')}>
            <PerDatePicker
              inputDate={meetingDate}
              onDateChanged={(utcDateChanged) => onUpdate('meetingDate', utcDateChanged)}
              timeZoneAndLocale={timelocal}
              dateFormat={'DMY'}
              disabled={false}
              disabledDate={inviteModalContentUtils.isMeetingDateNotAvailable}
            />
          </InviteModalRow>

          <InviteModalRow label={t('meetingInvite.meetingTime')}>
            <PerTimePicker
              inputDate={meetingDate}
              timeZoneAndLocale={timelocal}
              onTimeChanged={(utcDateChanged) => onUpdate('meetingDate', utcDateChanged)}
              timeFormat="HH:mm"
              minuteStep={15}
              hideDisabledOptions={true}
            />
            <InviteModalMeetingOverlap duration={duration} meetingDate={meetingDate} />
          </InviteModalRow>
          <InviteModalRow label={t('meetingInvite.duration')}>
            <MeetingInviteDurationSelect
              duration={duration}
              onChangeDuration={(durationChanged) => onUpdate('duration', durationChanged)}
            />
          </InviteModalRow>

          <InviteModalRow label={t('meetingInvite.appraisalSeason')}>
            <AppraisalSeasonRow appraisalSeason={appraisalSeason!} showIcon={false} />
          </InviteModalRow>

          <InviteModalRow label={t('meetingInvite.meetingType')}>
            <MeetingInviteOptionsSelect
              onChangeMeetingType={(type) => onUpdate('meetingType', type)}
              inviteOptions={[state.meetingType]}
              meetingType={state.meetingType}
              isPredefined={true}
              shouldDisableAppraisals={false}
            />
          </InviteModalRow>

          <InviteModalRow label={t('meetingInvite.location')}>
            <Input
              type="text"
              name="location"
              value={location}
              onChange={({ target: { value } }) => onUpdate('location', value)}
              placeholder={t('meetingInvite.locationPlaceholder')}
              data-e2e-modal-meeting-location
            />
          </InviteModalRow>

          <InviteModalRow label={t('meetingInvite.additionalParticipants')} margin="bigger">
            <NonParticipantsSelect
              placeholderText={t('meetingInvite.additionalParticipantsPlaceholder')}
              notFoundContentText={t('meetingInvite.notFoundEmployee')}
              participantsOptions={additionalParticipantsOptions!}
              onChangeParticipant={(participant) => onUpdate('additionalParticipants', [participant])}
              additionalParticipantId={additionalParticipants.length ? additionalParticipants[0].id : undefined}
            />
          </InviteModalRow>

          <InviteModalRow label={t('meetingInvite.meetingAgenda')} align="top" margin={agendaMargin()}>
            {isAppraisalMeeting && viewIds && (
              <div data-testid="invite-modal-step-list">
                <AppraisalInviteSteps viewIds={viewIds} onCheckStep={(viewId, checked) => onUpdateSteps(viewId, checked)} />
              </div>
            )}

            {isAppraisalUpdateMeeting && viewIds && (
              <div data-testid="invite-modal-step-list">
                <AppraisalUpdateInviteSteps viewIds={viewIds} onCheckStep={(viewId, checked) => onUpdateSteps(viewId, checked)} />
              </div>
            )}
          </InviteModalRow>

          {appraisalWithReducedSteps && (
            <div data-testid="invite-modal-reason-for-reduced-steps">
              <InviteModalRow label={t('meetingInvite.reasonForReducedSteps.header')} margin="bigger">
                <ReducedStepsReasonSelect
                  reasonForReducedSteps={reasonForReducedSteps}
                  onSetReason={(reason: string) => onUpdate('reasonForReducedSteps', reason)}
                />
              </InviteModalRow>
            </div>
          )}

          <InviteModalRow label={t('meetingInvite.emailText')} align="top" margin="no">
            <div data-e2e-modal-meeting-email-textarea data-testid="invite-modal-email">
              <RichTextBox text={emailText ? emailText : ''} onChange={(text) => onUpdate('emailText', text)} />
            </div>
          </InviteModalRow>
        </>
      </div>
      {serverError && (
        <StatusMessageBox
          variant="error"
          withTopMargin
          message={
            serverError.some((error) => error.errorKey === 'permission.not.granted.30095')
              ? t(`meetingInvite.reviewerNotAuthorized`)
              : serverError[0] && serverError[0].message
              ? serverError[0].message
              : t('global.notifications.error.description')
          }
        />
      )}
    </>
  )
}

export default DashboardAppraisalInviteModalContent
